// src/dataObject/DataObject.ts
import {
  generateRowId,
  isMapField,
  isReadonlyField as isReadonlyField2,
  STRING_FIELD_TYPES,
  NUMERIC_FIELD_TYPES
} from "@xone-web/types";

// src/dirty/DirtyTracker.ts
var DirtyTracker = class {
  original = /* @__PURE__ */ new Map();
  dirtySet = /* @__PURE__ */ new Set();
  /** Inicializa el snapshot original con los valores cargados desde BD. */
  setOriginal(fields) {
    this.original.clear();
    if (fields instanceof Map) {
      for (const [k, v] of fields) this.original.set(k, v);
    } else {
      for (const [k, v] of Object.entries(fields)) this.original.set(k, v);
    }
    this.dirtySet.clear();
  }
  /** Asignacion: marca como dirty si difiere del valor original. */
  track(field, oldValue, newValue) {
    if (!this.original.has(field)) {
      this.original.set(field, oldValue);
    }
    const originalValue = this.original.get(field);
    if (sameValue(originalValue, newValue)) {
      this.dirtySet.delete(field);
    } else {
      this.dirtySet.add(field);
    }
  }
  /** Marca un campo como dirty sin alterar el snapshot (uso interno). */
  markDirty(field) {
    this.dirtySet.add(field);
  }
  isDirty() {
    return this.dirtySet.size > 0;
  }
  isPropertyDirty(field) {
    return this.dirtySet.has(field);
  }
  getDirtyProperties() {
    return [...this.dirtySet];
  }
  getOldValue(field) {
    return this.original.get(field);
  }
  /** Tras un `save` exitoso, el estado actual pasa a ser el original. */
  commit(currentFields) {
    this.original.clear();
    for (const [k, v] of currentFields) this.original.set(k, v);
    this.dirtySet.clear();
  }
  /** Tras `refresh`, restaura snapshot completo. */
  reset(fields) {
    this.setOriginal(fields);
  }
  /**
   * Limpia el flag dirty de un único campo (utilizado por `refresh(propName)`,
   * paridad XOne 1.3.0). No altera el snapshot original.
   */
  clearProperty(field) {
    this.dirtySet.delete(field);
  }
};
function sameValue(a, b) {
  if (Object.is(a, b)) return true;
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  return false;
}

// src/proxy/DataObjectProxy.ts
import { isReadonlyField } from "@xone-web/types";

// src/errors/XoneDataError.ts
var XONE_DATA_ERROR_CODES = {
  /** Acceso a campo en minusculas (`self.codigo` lanza esto). */
  FIELD_CASE_INVALID: "XONE.JS.FIELD_CASE_INVALID",
  /** Intento de escribir un campo MAP_* (read-only). */
  READONLY_FIELD: "XONE.DATA.READONLY_FIELD",
  /** Coleccion no cargada cuando una operacion la requiere. */
  NOT_LOADED: "XONE.DATA.NOT_LOADED",
  /** Cursor browse no abierto / ya cerrado. */
  BROWSE_INVALID_STATE: "XONE.DATA.BROWSE_INVALID_STATE",
  /** Storage adapter no inyectado. */
  NO_STORAGE_ADAPTER: "XONE.DATA.NO_STORAGE_ADAPTER",
  /** Argumento invalido (vacio, tipo incorrecto). */
  INVALID_ARGUMENT: "XONE.DATA.INVALID_ARGUMENT",
  /** Item no encontrado. */
  NOT_FOUND: "XONE.DATA.NOT_FOUND",
  /** Operacion cancelada por `<before-save>`/`<before-delete>`. */
  CANCELED_BY_EVENT: "XONE.DATA.CANCELED_BY_EVENT",
  /** JSON invalido en `loadFromJson`. */
  JSON_INVALID: "XONE.DATA.JSON_INVALID",
  /** Coleccion locked. */
  LOCKED: "XONE.DATA.LOCKED"
};
var XoneDataError = class _XoneDataError extends Error {
  code;
  field;
  collection;
  cause;
  constructor(message, init) {
    super(message);
    this.name = "XoneDataError";
    this.code = init.code;
    this.field = init.field;
    this.collection = init.collection;
    this.cause = init.cause;
    Object.setPrototypeOf(this, _XoneDataError.prototype);
  }
};

// src/proxy/DataObjectProxy.ts
var XONE_API_ALIASES = {
  ownerCollection: { getter: "getOwnerCollection", setter: "setOwnerCollection" },
  OwnerCollection: { getter: "getOwnerCollection", setter: "setOwnerCollection" },
  ownerApp: { getter: "getOwnerApp" },
  OwnerApp: { getter: "getOwnerApp" },
  contentsCount: { getter: "getContentsCount" },
  ContentsCount: { getter: "getContentsCount" },
  dirty: { getter: "getDirty" },
  Dirty: { getter: "getDirty" }
};
function looksLikeFieldAccess(prop, target) {
  if (typeof prop !== "string") return false;
  if (prop.length === 0) return false;
  if (prop in target) return false;
  if (prop === "then" || prop === "constructor" || prop === "prototype") return false;
  if (prop.startsWith("_")) return false;
  return /^[A-Za-z][A-Za-z0-9_]*$/.test(prop);
}
function assertUpperCaseField(field) {
  if (!/^[A-Z][A-Z0-9_]*$/.test(field)) {
    throw new XoneDataError(
      `El nombre de campo '${field}' debe estar en MAYUSCULAS con underscores. Los identificadores en minusculas no son campos validos del modelo XOne.`,
      {
        code: XONE_DATA_ERROR_CODES.FIELD_CASE_INVALID,
        field
      }
    );
  }
}
function wrapWithProxy(target) {
  return new Proxy(target, {
    get(t, prop, receiver) {
      if (typeof prop === "string") {
        const alias = XONE_API_ALIASES[prop];
        if (alias) {
          const fn = t[alias.getter];
          if (typeof fn === "function") {
            return fn.call(t);
          }
          return fn;
        }
      }
      if (!looksLikeFieldAccess(prop, t)) {
        return Reflect.get(t, prop, receiver);
      }
      const field = prop;
      assertUpperCaseField(field);
      return t.getValue(field);
    },
    set(t, prop, value, receiver) {
      if (typeof prop === "string") {
        const alias = XONE_API_ALIASES[prop];
        if (alias) {
          if (alias.setter) {
            const fn = t[alias.setter];
            if (typeof fn === "function") {
              fn.call(t, value);
              return true;
            }
          }
          throw new XoneDataError(
            `La propiedad '${prop}' es read-only.`,
            {
              code: XONE_DATA_ERROR_CODES.READONLY_FIELD,
              field: prop
            }
          );
        }
      }
      if (!looksLikeFieldAccess(prop, t)) {
        return Reflect.set(t, prop, value, receiver);
      }
      const field = prop;
      assertUpperCaseField(field);
      if (isReadonlyField(field)) {
        throw new XoneDataError(
          `El campo '${field}' es read-only.`,
          {
            code: XONE_DATA_ERROR_CODES.READONLY_FIELD,
            field
          }
        );
      }
      t.setValue(field, value);
      return true;
    },
    has(t, prop) {
      if (typeof prop !== "string") return Reflect.has(t, prop);
      if (prop in t) return true;
      if (prop in XONE_API_ALIASES) return true;
      return t.hasField(prop);
    },
    ownKeys(t) {
      const own = Reflect.ownKeys(t);
      const fieldNames = t.getPropertyNames();
      return Array.from(/* @__PURE__ */ new Set([...own, ...fieldNames]));
    },
    getOwnPropertyDescriptor(t, prop) {
      if (typeof prop === "string" && t.hasField(prop) && !(prop in t)) {
        return {
          configurable: true,
          enumerable: true,
          writable: !isReadonlyField(prop),
          value: t.getValue(prop)
        };
      }
      return Reflect.getOwnPropertyDescriptor(t, prop);
    }
  });
}

// src/dataObject/DataObject.ts
var DataObject = class {
  fields = /* @__PURE__ */ new Map();
  variables = /* @__PURE__ */ new Map();
  contentsCache = /* @__PURE__ */ new Map();
  // Renombrado a `_dirty` (con underscore) para que el `DataObjectProxy`
  // pueda exponer `self.dirty` como alias de `getDirty()` (paridad
  // `XoneDataObject.WrapNormalProperty` Android) sin colisionar con este
  // tracker interno cuando un metodo de la clase se invoca con `this=proxy`.
  _dirty = new DirtyTracker();
  _isNew;
  _owner;
  _parent;
  /** Indice dentro de la coleccion (gestionado por DataCollection). */
  _index = -1;
  constructor(init) {
    this._owner = init.owner;
    this._parent = init.parent ?? null;
    this._isNew = init.isNew;
    const def = this._owner.getDefinition();
    for (const field of def.fields) {
      this.fields.set(field.name, field.defaultValue ?? null);
    }
    if (this._isNew && !this.fields.has("ROWID")) {
      this.fields.set("ROWID", generateRowId());
    }
    if (init.initial) {
      for (const [k, v] of Object.entries(init.initial)) {
        this.fields.set(k, v);
      }
      if (this._isNew && !this.fields.get("ROWID")) {
        this.fields.set("ROWID", generateRowId());
      }
    }
    this._dirty.setOriginal(this.fields);
  }
  // -------------------------------------------------------------------------
  // Identificacion y navegacion (SYNC)
  // -------------------------------------------------------------------------
  getOwnerApp() {
    return this._owner.getOwnerApp();
  }
  getOwnerCollection() {
    return this._owner;
  }
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#setOwnerCollection`): el parámetro es
   * OBLIGATORIO y debe ser una instancia `DataCollection`. Devuelve `null`
   * en el wrapper Java; aquí devolvemos `void` (TS). Lanza si `coll` no es
   * una DataCollection.
   */
  setOwnerCollection(coll) {
    if (!coll || typeof coll.getName !== "function") {
      throw new XoneDataError(
        "setOwnerCollection: se requiere una instancia DataCollection.",
        {
          code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
          collection: this._owner.getName()
        }
      );
    }
    this._owner = coll;
  }
  getParent() {
    return this._parent;
  }
  getObjectIndex() {
    return this._index;
  }
  isNew() {
    return this._isNew;
  }
  toString() {
    const id = this.fields.get("ROWID") ?? this.fields.get("CODIGO") ?? "?";
    return `[DataObject ${this._owner.getName()}#${id}]`;
  }
  // -------------------------------------------------------------------------
  // Valores (SYNC)
  // -------------------------------------------------------------------------
  /** Indica si el campo existe en la definicion o en los datos cargados. */
  hasField(field) {
    if (this.fields.has(field)) return true;
    return this._owner.getDefinition().fields.some((f) => f.name === field);
  }
  get(fieldName) {
    return this.getValue(fieldName);
  }
  getValue(fieldName) {
    assertUpperCaseField(fieldName);
    if (!this.fields.has(fieldName) && !this.hasField(fieldName)) {
      return void 0;
    }
    const v = this.fields.get(fieldName);
    if (v !== void 0 && v !== null) return v;
    const def = this._owner.getDefinition().fields.find((f) => f.name === fieldName);
    if (def) {
      const type = def.type;
      if (STRING_FIELD_TYPES.has(type)) return "";
      if (NUMERIC_FIELD_TYPES.has(type)) return 0;
    }
    if (isMapField(fieldName)) return "";
    return v;
  }
  /**
   * Snapshot inmutable de todos los campos (paridad XOne: lectura completa
   * del row para alimentar la resolucion de macros `##FLD_*##`).
   *
   * Devuelve una copia plana (clave: nombre del campo en MAYUSCULAS, valor:
   * el unknown sin tipar). La mutacion del Map devuelto NO afecta al
   * DataObject. Pensado para integraciones que necesitan inspeccionar el row
   * entero — p.ej. el `xone-xml-renderer` al expandir
   * `<prop title="##FLD_*##">` antes de pintar.
   *
   * Coste: O(N) sobre el numero de campos cargados (~10-100 por row tipico).
   */
  getAllFields() {
    const out = {};
    const declared = this._owner.getDefinition().fields;
    for (const [k, v] of this.fields) {
      out[k] = v;
    }
    for (const def of declared) {
      const cur = out[def.name];
      if (cur !== void 0 && cur !== null) continue;
      const type = def.type;
      if (STRING_FIELD_TYPES.has(type)) {
        out[def.name] = "";
      } else if (NUMERIC_FIELD_TYPES.has(type)) {
        out[def.name] = 0;
      }
    }
    return out;
  }
  setValue(field, value) {
    assertUpperCaseField(field);
    if (isReadonlyField2(field)) {
      throw new XoneDataError(
        `El campo '${field}' es read-only.`,
        {
          code: XONE_DATA_ERROR_CODES.READONLY_FIELD,
          field,
          collection: this._owner.getName()
        }
      );
    }
    if (value === void 0) return;
    const oldValue = this.fields.get(field);
    this.fields.set(field, value);
    this._dirty.track(field, oldValue, value);
    const bus = this._owner._getBus();
    if (bus && bus.hasHandlers(`onchange:${field}`)) {
      void bus.emitObject(`onchange:${field}`, {
        self: this,
        locals: { field, oldValue, newValue: value }
      });
    }
  }
  /**
   * Variante que espera al bus `<onchange>` para coordinar handlers async.
   * El generador puede insertar `await self.setValueAsync(...)` cuando el
   * `<onchange>` tenga side-effects que deben completarse antes de seguir.
   */
  async setValueAsync(field, value) {
    assertUpperCaseField(field);
    if (isReadonlyField2(field)) {
      throw new XoneDataError(
        `El campo '${field}' es read-only.`,
        {
          code: XONE_DATA_ERROR_CODES.READONLY_FIELD,
          field,
          collection: this._owner.getName()
        }
      );
    }
    if (value === void 0) return;
    const oldValue = this.fields.get(field);
    this.fields.set(field, value);
    this._dirty.track(field, oldValue, value);
    const bus = this._owner._getBus();
    if (bus) {
      await bus.emitObject(`onchange:${field}`, {
        self: this,
        locals: { field, oldValue, newValue: value }
      });
    }
  }
  getAllContentNames() {
    return (this._owner.getDefinition().contents ?? []).map((c) => c.name);
  }
  getFieldPropertyValue(field, attribute) {
    const def = this._owner.getDefinition().fields.find((f) => f.name === field);
    if (!def) return void 0;
    const known = def[attribute];
    if (known !== void 0) return known;
    return def.attributes?.[attribute];
  }
  setFieldPropertyValue(_field, _attribute, value) {
    return value;
  }
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#getNodePropertyValue`): 3 o 4 parámetros.
   * Devuelve el valor del atributo `attribute` del item `itemName` situado dentro
   * del nodo `nodeName` declarado en la colección. Si `evaluate=true`, evalúa la
   * expresión XOne contenida en el atributo. MVP: lookup directo en la definición.
   */
  getNodePropertyValue(_nodeName, _itemName, _attribute, _evaluate) {
    return void 0;
  }
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#setNodePropertyValue`): requiere 4 params
   * `(nodeName, itemName, attribute, value)`. Modifica en runtime el atributo XML.
   * MVP: no-op (la mutación efectiva la hace `14-xml-renderer`).
   */
  setNodePropertyValue(nodeName, itemName, attribute, _value) {
    if (!nodeName || !itemName || !attribute) {
      throw new XoneDataError(
        "setNodePropertyValue: nodeName, itemName y attribute son obligatorios.",
        {
          code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
          collection: this._owner.getName()
        }
      );
    }
  }
  getContentAttr(contentsName, attrName) {
    const def = this._owner.getDefinition().contents?.find(
      (c) => c.name === contentsName
    );
    if (!def) return "";
    if (attrName === "src" || attrName === "coll") return def.src;
    return def.attributes?.[attrName] ?? "";
  }
  // -------------------------------------------------------------------------
  // Variables (SYNC)
  // -------------------------------------------------------------------------
  getVariable(name) {
    return this.variables.get(name);
  }
  getVariables(name) {
    return this.getVariable(name);
  }
  setVariable(name, value) {
    this.variables.set(name, value);
  }
  setVariables(name, value) {
    this.setVariable(name, value);
  }
  getAllVariables() {
    return Object.fromEntries(this.variables);
  }
  // -------------------------------------------------------------------------
  // Dirty (SYNC)
  // -------------------------------------------------------------------------
  getDirty() {
    return this._dirty.isDirty();
  }
  isPropertyDirty(field) {
    return this._dirty.isPropertyDirty(field);
  }
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#getDirtyProperties`): los parámetros se
   * ignoran (la firma Java acepta `Object... params` pero no se consultan). Devuelve
   * el array (vacío si no hay dirty).
   */
  getDirtyProperties(..._ignored) {
    return this._dirty.getDirtyProperties();
  }
  getOldValue(field) {
    return this._dirty.getOldValue(field);
  }
  getOldItem(field) {
    return this.getOldValue(field);
  }
  getContentsCount() {
    return (this._owner.getDefinition().contents ?? []).length;
  }
  // -------------------------------------------------------------------------
  // Persistencia (ASYNC)
  // -------------------------------------------------------------------------
  async save() {
    const bus = this._owner._getBus();
    if (bus) {
      const r = await bus.emitObject("before-save", { self: this });
      if (r.canceled) return false;
    }
    const storage = this._owner._getStorage();
    const def = this._owner.getDefinition();
    const tableName = (def.tablePrefix ?? "") + def.tableName;
    if (this._isNew) {
      const fields = def.fields.map((f) => f.name).filter((n) => !isMapField(n));
      const sqlCols = fields.map((n) => n.toLowerCase());
      const placeholders = fields.map(() => "?");
      const values = fields.map((name) => this.fields.get(name) ?? null);
      const sql = `INSERT INTO ${tableName} (${sqlCols.join(", ")}) VALUES (${placeholders.join(", ")})`;
      await storage.executeSql(sql, values);
      this._isNew = false;
    } else {
      const dirtyFields = this._dirty.getDirtyProperties().filter((n) => !isMapField(n));
      if (dirtyFields.length === 0) {
        if (bus) await bus.emitObject("after-save", { self: this });
        return true;
      }
      const idField = def.idFieldName ?? "ROWID";
      const setSql = dirtyFields.map((f) => `${f.toLowerCase()} = ?`).join(", ");
      const values = dirtyFields.map((f) => this.fields.get(f) ?? null);
      values.push(this.fields.get(idField) ?? null);
      const sql = `UPDATE ${tableName} SET ${setSql} WHERE ${idField.toLowerCase()} = ?`;
      await storage.executeSql(sql, values);
    }
    this._dirty.commit(this.fields);
    if (bus) await bus.emitObject("after-save", { self: this });
    return true;
  }
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#deleteObject`): el wrapper Java acepta
   * `Object... params` pero los IGNORA y llama a `DeleteObject()` sin argumentos.
   * Aceptamos varargs por compat con la firma legacy `deleteObject(options)`.
   */
  async deleteObject(..._ignored) {
    const bus = this._owner._getBus();
    if (bus) {
      const r = await bus.emitObject("before-delete", { self: this });
      if (r.canceled) {
        throw new XoneDataError("Borrado cancelado por <before-delete>.", {
          code: XONE_DATA_ERROR_CODES.CANCELED_BY_EVENT,
          collection: this._owner.getName()
        });
      }
    }
    const storage = this._owner._getStorage();
    const def = this._owner.getDefinition();
    const tableName = (def.tablePrefix ?? "") + def.tableName;
    const idField = def.idFieldName ?? "ROWID";
    const sql = `DELETE FROM ${tableName} WHERE ${idField.toLowerCase()} = ?`;
    await storage.executeSql(sql, [this.fields.get(idField) ?? null]);
    this._owner._removeFromItems(this);
    if (bus) await bus.emitObject("after-delete", { self: this });
    return true;
  }
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#refresh`): 0 o 1 parámetro.
   * - Sin argumentos: relee TODOS los campos desde BD descartando cambios en memoria.
   * - Con un string `propName`: refresca únicamente ese campo.
   * - Si se pasa un valor no-string, lanza `XoneGenericException` (paridad mobile).
   * - Más de 1 parámetro lanza error.
   */
  async refresh(propName) {
    if (propName !== void 0 && typeof propName !== "string") {
      throw new XoneDataError(
        "refresh: el par\xE1metro debe ser un string (nombre de campo) o omitirse.",
        {
          code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
          collection: this._owner.getName()
        }
      );
    }
    if (this._isNew) return false;
    const storage = this._owner._getStorage();
    const def = this._owner.getDefinition();
    const tableName = (def.tablePrefix ?? "") + def.tableName;
    const idField = def.idFieldName ?? "ROWID";
    const sql = `SELECT * FROM ${tableName} WHERE ${idField.toLowerCase()} = ? LIMIT 1`;
    const result = await storage.executeSql(sql, [this.fields.get(idField) ?? null]);
    const row = result.rows[0];
    if (!row) return false;
    if (propName !== void 0) {
      if (Object.prototype.hasOwnProperty.call(row, propName)) {
        this.fields.set(propName, row[propName]);
        this._dirty.clearProperty(propName);
      }
      return true;
    }
    this.fields.clear();
    for (const [k, v] of Object.entries(row)) this.fields.set(k, v);
    this._dirty.reset(this.fields);
    return true;
  }
  clearCaches() {
    this.contentsCache.clear();
  }
  // -------------------------------------------------------------------------
  // Contenidos (ASYNC)
  // -------------------------------------------------------------------------
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#contents`): acepta EXACTAMENTE 1 parámetro
   * — el nombre del `<contents>` (string) o su índice posicional (número). No hay
   * `options`. Lanza si se pasan 0 o más de 1.
   */
  async contents(nameOrIndex) {
    return this.getContents(nameOrIndex);
  }
  async getContents(nameOrIndex) {
    if (nameOrIndex === void 0 || nameOrIndex === null) {
      throw new XoneDataError(
        "contents: se requiere el nombre o \xEDndice del <contents>.",
        {
          code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
          collection: this._owner.getName()
        }
      );
    }
    let name;
    if (typeof nameOrIndex === "number") {
      const list = this._owner.getDefinition().contents ?? [];
      const idx = nameOrIndex;
      if (idx < 0 || idx >= list.length) {
        throw new XoneDataError(
          `contents: \xEDndice ${idx} fuera de rango.`,
          {
            code: XONE_DATA_ERROR_CODES.NOT_FOUND,
            collection: this._owner.getName()
          }
        );
      }
      name = list[idx].name;
    } else {
      name = nameOrIndex;
    }
    const cached = this.contentsCache.get(name);
    if (cached) return cached;
    const child = await this._owner._buildContents(name, this);
    this.contentsCache.set(name, child);
    return child;
  }
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#getObjectItem`): acepta 1 o 2 strings
   * `(field, [contentsName])`. Devuelve el `DataObject` referenciado por una
   * propiedad con foreign key. Fuera de ese rango lanza error. MVP: no-op.
   */
  async getObjectItem(field, contentsName) {
    if (!field || typeof field !== "string") {
      throw new XoneDataError(
        "getObjectItem: se requiere el nombre del campo (FK).",
        {
          code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
          collection: this._owner.getName()
        }
      );
    }
    if (contentsName !== void 0 && typeof contentsName !== "string") {
      throw new XoneDataError(
        "getObjectItem: contentsName debe ser un string.",
        {
          code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
          collection: this._owner.getName()
        }
      );
    }
    return null;
  }
  // -------------------------------------------------------------------------
  // Acciones (ASYNC)
  // -------------------------------------------------------------------------
  async executeNode(nodeName, ..._args) {
    const bus = this._owner._getBus();
    if (!bus) return void 0;
    const r = await bus.emitCustomNode(nodeName, { self: this });
    return r.returnValue;
  }
  // -------------------------------------------------------------------------
  // Metadatos (SYNC)
  // -------------------------------------------------------------------------
  getPropertyNames() {
    return this._owner.getDefinition().fields.map((f) => f.name);
  }
  getPropertyTitle(name) {
    const def = this._owner.getDefinition().fields.find((f) => f.name === name);
    return def?.title ?? name;
  }
  getPropertyGroup(name) {
    const def = this._owner.getDefinition().fields.find((f) => f.name === name);
    return def?.group ?? "";
  }
  // -------------------------------------------------------------------------
  // Clonacion / JSON
  // -------------------------------------------------------------------------
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#clone`): el único parámetro opcional es
   * `copyId: boolean` (no un objeto `options`). Si `copyId=true` conserva ROWID e
   * id del original; por defecto el clon es independiente y obtiene ROWID nuevo.
   */
  async clone(copyId) {
    const data = Object.fromEntries(this.fields);
    if (copyId !== true) {
      data["ROWID"] = generateRowId();
    }
    const cloned = this._owner._instantiate({ ...data }, true);
    return cloned;
  }
  /** SYNC: serializacion plana en memoria. */
  toJson(_options) {
    return Object.fromEntries(this.fields);
  }
  /** SYNC. */
  toJsonString() {
    return JSON.stringify(this.toJson(), (_k, v) => {
      if (v instanceof Date) return v.toISOString();
      return v;
    });
  }
  /**
   * Aplica un JSON al objeto. ASYNC porque puede insertar en BD si la
   * coleccion espera persistencia inmediata (paridad README §5b.1).
   *
   * Aqui la implementacion estandar es: cargar campos en memoria y devolver
   * el `DataObject`. Si el llamante quiere persistir, llama `save()`.
   */
  async loadFromJson(json) {
    let obj;
    try {
      obj = typeof json === "string" ? JSON.parse(json) : json;
    } catch (err) {
      throw new XoneDataError("JSON invalido en loadFromJson.", {
        code: XONE_DATA_ERROR_CODES.JSON_INVALID,
        cause: err
      });
    }
    if (obj === null || typeof obj !== "object") {
      throw new XoneDataError("loadFromJson espera un objeto.", {
        code: XONE_DATA_ERROR_CODES.JSON_INVALID
      });
    }
    for (const [k, v] of Object.entries(obj)) {
      try {
        assertUpperCaseField(k);
      } catch (err) {
        throw err;
      }
      this.fields.set(k, v);
    }
    this._dirty.setOriginal(this.fields);
    return this;
  }
  // -------------------------------------------------------------------------
  // Binding (stubs - plan 08 §13.3, MVP)
  // -------------------------------------------------------------------------
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#bind`): 3 o 4 parámetros
   * `(controlName, eventName, callback, [context])`. Registra dinámicamente un
   * manejador JavaScript para un evento de un control. Lanza si los nombres
   * están vacíos. MVP: no-op (los handlers reales viven en `04-events`).
   */
  bind(controlName, eventName, _callback, _context) {
    if (!controlName || typeof controlName !== "string") {
      throw new XoneDataError(
        "bind: controlName es obligatorio.",
        {
          code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
          collection: this._owner.getName()
        }
      );
    }
    if (!eventName || typeof eventName !== "string") {
      throw new XoneDataError(
        "bind: eventName es obligatorio.",
        {
          code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
          collection: this._owner.getName()
        }
      );
    }
  }
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#unbind`): requiere EXACTAMENTE 2 strings
   * `(controlName, eventName)`. Elimina todos los manejadores asociados.
   */
  unbind(controlName, eventName) {
    if (!controlName || typeof controlName !== "string") {
      throw new XoneDataError(
        "unbind: controlName es obligatorio.",
        {
          code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
          collection: this._owner.getName()
        }
      );
    }
    if (!eventName || typeof eventName !== "string") {
      throw new XoneDataError(
        "unbind: eventName es obligatorio.",
        {
          code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
          collection: this._owner.getName()
        }
      );
    }
  }
};
function createDataObject(init) {
  const raw = new DataObject(init);
  return wrapWithProxy(raw);
}

// src/dataCollection/DataCollection.ts
import { CollectionMacroStore, replaceMacrosSql } from "@xone-web/macros";
import { generateRowId as generateRowId2 } from "@xone-web/types";

// src/sql/developFieldValueMacros.ts
function developFieldValueMacros(sentence, dataObject, options = {}) {
  if (!sentence) return "";
  if (!dataObject) return sentence;
  const prefix = options.prefix ?? "FLD_";
  const suppress = options.suppressQuotes ?? true;
  const re = new RegExp(
    `('?)##${escapeRegex(prefix)}([A-Z0-9_]+)##('?)`,
    "g"
  );
  return sentence.replace(re, (_match, qL, fieldName, qR) => {
    let value;
    try {
      value = dataObject.getValue(fieldName);
    } catch {
      value = void 0;
    }
    const developed = developObjectValue(value);
    if (suppress) {
      return developed;
    }
    return `${qL}${developed}${qR}`;
  });
}
function developObjectValue(value) {
  if (value === null || value === void 0) return "NULL";
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "NULL";
    return String(value);
  }
  if (typeof value === "boolean") {
    return value ? "1" : "0";
  }
  if (typeof value === "bigint") {
    return value.toString();
  }
  if (value instanceof Date) {
    const pad = (n) => String(n).padStart(2, "0");
    const y = value.getUTCFullYear();
    const m = pad(value.getUTCMonth() + 1);
    const d = pad(value.getUTCDate());
    const hh = pad(value.getUTCHours());
    const mm = pad(value.getUTCMinutes());
    const ss = pad(value.getUTCSeconds());
    return `'${y}-${m}-${d} ${hh}:${mm}:${ss}'`;
  }
  const s = String(value);
  return `'${s.replace(/'/g, "''")}'`;
}
function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// src/dsl/DslBuilder.ts
var DslBuilder = class {
  onMissing;
  constructor(opts = {}) {
    this.onMissing = opts.onMissingValue ?? "drop";
  }
  /**
   * Resuelve el input completo. Si `input.filterTemplate` no produce ningún
   * leaf válido tras aplicar la política `onMissingValue=drop`, el resultado
   * no incluye `filter`.
   */
  build(input, context) {
    const out = {};
    if (input.filterTemplate !== void 0) {
      const resolved = this.resolveNode(input.filterTemplate, context);
      if (resolved !== null) out.filter = resolved;
    }
    if (input.order !== void 0) out.order = input.order;
    if (input.page !== void 0) out.page = input.page;
    if (input.project !== void 0) out.project = input.project;
    return out;
  }
  // -- Internos ------------------------------------------------------------
  resolveNode(node, ctx) {
    if (isLeaf(node)) {
      return this.resolveLeaf(node, ctx);
    }
    return this.resolveLogical(node, ctx);
  }
  resolveLeaf(leaf, ctx) {
    if (leaf.op === "IsNull" || leaf.op === "IsNotNull") {
      return { field: leaf.field, op: leaf.op };
    }
    const resolved = this.resolveValue(leaf.value, ctx, leaf.field, leaf.op);
    if (resolved === MISSING) return null;
    return { field: leaf.field, op: leaf.op, value: resolved };
  }
  resolveLogical(node, ctx) {
    if (node.and) {
      const children = this.resolveChildren(node.and, ctx);
      if (children.length === 0) return null;
      if (children.length === 1) return children[0];
      return { and: children };
    }
    if (node.or) {
      const children = this.resolveChildren(node.or, ctx);
      if (children.length === 0) return null;
      if (children.length === 1) return children[0];
      return { or: children };
    }
    if (node.not) {
      const child = this.resolveNode(node.not, ctx);
      return child === null ? null : { not: child };
    }
    return null;
  }
  resolveChildren(nodes, ctx) {
    const out = [];
    for (const child of nodes) {
      const r = this.resolveNode(child, ctx);
      if (r !== null) out.push(r);
    }
    return out;
  }
  resolveValue(expr, ctx, field, op) {
    if (expr === void 0) {
      return this.handleMissing(`${field}/${op}`);
    }
    if (expr === null || typeof expr !== "object") {
      return expr;
    }
    if (Array.isArray(expr)) {
      return expr;
    }
    if ("$ctx" in expr) {
      const v = ctx.getCtxField(expr.$ctx);
      if (v === void 0) return this.handleMissing(`$ctx.${expr.$ctx}`);
      return v;
    }
    if ("$macro" in expr) {
      const v = ctx.getGlobalMacro(expr.$macro);
      if (v === void 0 || v === null) return this.handleMissing(`$macro.${expr.$macro}`);
      return v;
    }
    return expr;
  }
  handleMissing(token) {
    switch (this.onMissing) {
      case "drop":
        return MISSING;
      case "null":
        return null;
      case "throw":
        throw new Error(`[DslBuilder] placeholder no resuelto: ${token}`);
    }
  }
};
var MISSING = /* @__PURE__ */ Symbol("MISSING");
function isLeaf(node) {
  return node.field !== void 0 && node.op !== void 0;
}

// src/dataCollection/DataCollection.ts
var DataCollection = class _DataCollection {
  _name;
  _definition;
  _storage;
  _bus;
  _runtime;
  _appData;
  _parent;
  _logger;
  _macros;
  _globalMacros;
  _variables = /* @__PURE__ */ new Map();
  _structuredClient;
  _dslBuilder;
  _items = [];
  _currentIndex = -1;
  _filter = "";
  _linkFilter = "";
  _sort = "";
  _fullyLoaded = false;
  _locked = false;
  _cursor = null;
  /**
   * DataObject "owner" del contexto en el que se evalúan las macros
   * `##FLD_X##` y `##OWN_X##`. Réplica del framework Android: cuando un
   * `<contents>` está embebido en una pantalla, ese owner es el DataObject
   * de la pantalla, no las filas de la colección hija. Lo inyecta el
   * bootstrap del SPA antes de `loadAll`/`reload`.
   */
  _fieldMacroOwner = null;
  constructor(opts) {
    this._name = opts.name;
    this._definition = opts.definition;
    this._storage = opts.storage;
    this._bus = opts.bus;
    this._runtime = opts.runtime;
    this._appData = opts.appData;
    this._parent = opts.parent;
    this._logger = opts.logger ?? console;
    this._globalMacros = opts.macros;
    this._macros = new CollectionMacroStore();
    this._structuredClient = opts.structuredClient;
    this._dslBuilder = new DslBuilder({ onMissingValue: "drop" });
    if (opts.definition.defaultFilter) this._filter = opts.definition.defaultFilter;
    if (opts.definition.defaultSort) this._sort = opts.definition.defaultSort;
  }
  // -------------------------------------------------------------------------
  // Acceso interno (uso de DataObject y factory)
  // -------------------------------------------------------------------------
  /** @internal */
  _getStorage() {
    return this._storage;
  }
  /** @internal */
  _getBus() {
    return this._bus;
  }
  /** @internal */
  _removeFromItems(obj) {
    const idx = this._items.indexOf(obj);
    if (idx >= 0) this._items.splice(idx, 1);
    this._reindex();
  }
  /** @internal: instancia un DataObject pertenecente a esta coleccion. */
  _instantiate(initial, isNew) {
    const normalized = {};
    for (const [k, v] of Object.entries(initial)) {
      normalized[k.toUpperCase()] = v;
    }
    const wrapped = createDataObject({ owner: this, initial: normalized, isNew });
    return wrapped;
  }
  /** @internal: crea/clona una coleccion hija para `<contents>`. */
  async _buildContents(name, parent) {
    const def = this._definition.contents?.find((c) => c.name === name);
    if (!def) {
      throw new XoneDataError(`Contents '${name}' no definido en '${this._name}'.`, {
        code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
        collection: this._name
      });
    }
    const childDef = {
      name: def.src,
      tableName: def.src.toLowerCase(),
      tablePrefix: this._definition.tablePrefix,
      fields: [],
      contents: []
    };
    const child = new _DataCollection({
      name: def.src,
      definition: childDef,
      storage: this._storage,
      macros: this._globalMacros,
      ...this._bus ? { bus: this._bus } : {},
      appData: this._appData,
      parent
    });
    return child;
  }
  // -------------------------------------------------------------------------
  // Identificacion (SYNC)
  // -------------------------------------------------------------------------
  getName() {
    return this._name;
  }
  name() {
    return this._name;
  }
  getOwnerApp() {
    return this._appData;
  }
  getOwnerObject() {
    return this._parent ?? null;
  }
  ownerObject() {
    return this.getOwnerObject();
  }
  getIdFieldName() {
    return this._definition.idFieldName ?? "ROWID";
  }
  getCount() {
    return this._items.length;
  }
  count() {
    return this.getCount();
  }
  isFull() {
    return this._fullyLoaded;
  }
  isLocked() {
    return this._locked;
  }
  isBrowsing() {
    return this._cursor?.active === true;
  }
  stringKey() {
    const idField = this.getIdFieldName();
    const def = this._definition.fields.find((f) => f.name === idField);
    return def?.type === "T" || def?.type === "X" || idField === "ROWID";
  }
  getMultipleKey() {
    return false;
  }
  getCurrentItem() {
    if (this._cursor?.active) {
      const row = this._cursor.rows[this._cursor.index];
      if (!row) return null;
      return this._instantiate({ ...row }, false);
    }
    if (this._currentIndex >= 0 && this._currentIndex < this._items.length) {
      return this._items[this._currentIndex] ?? null;
    }
    return null;
  }
  getObjectIndex(item) {
    return this._items.indexOf(item);
  }
  /** ROWID 32 hex sin guiones (paridad XOne). */
  generateRowId() {
    return generateRowId2();
  }
  getXmlNode() {
    return this._definition.xmlNode;
  }
  getConnection() {
    return this._storage;
  }
  getDataConnector() {
    return this._storage;
  }
  getAccessString() {
    return this._buildSelectSql({ raw: true });
  }
  getDevelopedAccessString() {
    return this._buildSelectSql({ raw: false });
  }
  getDefinition() {
    return this._definition;
  }
  getAllowedEnterprises() {
    return this._definition.allowedEnterprises ?? "";
  }
  getAllowedUsers() {
    return this._definition.allowedUsers ?? "";
  }
  toString() {
    return `[DataCollection ${this._name} count=${this._items.length}]`;
  }
  // -------------------------------------------------------------------------
  // Filtro / sort (SYNC)
  // -------------------------------------------------------------------------
  getFilter() {
    return this._filter;
  }
  setFilter(value) {
    this._filter = value ?? "";
  }
  getLinkFilter() {
    return this._linkFilter;
  }
  setLinkFilter(value) {
    this._linkFilter = value ?? "";
  }
  getDevelopedFilter() {
    return this._developMacros(this._filter);
  }
  getDevelopedLinkFilter() {
    return this._developMacros(this._linkFilter);
  }
  /**
   * Inyecta el DataObject contra el que se resolverán las macros
   * `##FLD_<CAMPO>##` y `##OWN_<CAMPO>##` en `filter`, `linkFilter`, `sort`
   * y SQL custom. Réplica del comportamiento del framework Android.
   * Pasar `null` lo desconecta.
   */
  setFieldMacroOwner(owner) {
    this._fieldMacroOwner = owner;
  }
  getFieldMacroOwner() {
    return this._fieldMacroOwner;
  }
  getSort() {
    return this._sort;
  }
  setSort(value) {
    this._sort = value ?? "";
  }
  // -------------------------------------------------------------------------
  // Macros (SYNC)
  // -------------------------------------------------------------------------
  getMacro(name) {
    return this._macros.getMacro(name);
  }
  setMacro(name, value) {
    this._macros.setMacro(name, value);
  }
  getAllMacros() {
    return this._macros.getAllMacros();
  }
  getMacroCount() {
    return Object.keys(this._macros.getAllMacros()).length;
  }
  // -------------------------------------------------------------------------
  // Variables (SYNC)
  // -------------------------------------------------------------------------
  getVariable(name) {
    return this._variables.get(name);
  }
  setVariable(name, value) {
    this._variables.set(name, value);
  }
  /**
   * Alias plural de `getVariable` — paridad estricta
   * `XoneDataCollection.getVariables` (`xonedataobject_lib/.../
   * XoneDataCollection.java:5199`). Android expone los dos nombres como
   * `@ScriptAllowed`; muchos `.xne` legacy llaman al plural
   * (`self.ownerCollection.getVariables("##LOGIN_LASTUSER##")` —
   * `xone_projects/AllByXoneNew/LoginColl.xne:64`).
   */
  getVariables(name) {
    return this.getVariable(name);
  }
  /** Alias plural de `setVariable` — paridad Android. */
  setVariables(name, value) {
    this.setVariable(name, value);
  }
  getAllVariables() {
    return Object.fromEntries(this._variables);
  }
  // -------------------------------------------------------------------------
  // Metadatos (SYNC)
  // -------------------------------------------------------------------------
  getPropertyCount() {
    return this._definition.fields.length;
  }
  getPropertyName(index) {
    return this._definition.fields[index]?.name ?? "";
  }
  propertyName(index) {
    return this.getPropertyName(index);
  }
  getPropertyTitle(name) {
    return this._definition.fields.find((f) => f.name === name)?.title ?? name;
  }
  getPropertyGroup(name) {
    return this._definition.fields.find((f) => f.name === name)?.group ?? "";
  }
  getPropType(name) {
    return this._definition.fields.find((f) => f.name === name)?.type ?? "";
  }
  propType(name) {
    return this.getPropType(name);
  }
  getPropVisibility(name) {
    const v = this._definition.fields.find((f) => f.name === name)?.visibility;
    return v === void 0 ? "" : String(v);
  }
  propVisibility(name) {
    return this.getPropVisibility(name);
  }
  getGroupCount() {
    const groups = /* @__PURE__ */ new Set();
    for (const f of this._definition.fields) {
      if (f.group) groups.add(f.group);
    }
    return groups.size;
  }
  getGroup(index) {
    if (index === void 0) return "";
    const groups = Array.from(
      new Set(this._definition.fields.map((f) => f.group).filter(Boolean))
    );
    return groups[index] ?? "";
  }
  group(id) {
    if (typeof id === "number") return this.getGroup(id);
    return id;
  }
  getCollPropertyValue(name) {
    return this._definition[name] ?? "";
  }
  collPropertyValue(name) {
    return this.getCollPropertyValue(name);
  }
  // -------------------------------------------------------------------------
  // Carga / browse (ASYNC)
  // -------------------------------------------------------------------------
  async loadAll() {
    if (this._locked) {
      throw new XoneDataError(`Coleccion '${this._name}' esta locked.`, {
        code: XONE_DATA_ERROR_CODES.LOCKED,
        collection: this._name
      });
    }
    const rows = this._definition.linkedTo ? await this._loadViaLinkedTo() : await this._loadViaStorageAdapter();
    this._items = rows.map((row) => this._instantiate({ ...row }, false));
    this._reindex();
    this._fullyLoaded = true;
    this._currentIndex = this._items.length > 0 ? 0 : -1;
    if (this._bus) {
      for (const item of this._items) {
        await this._bus.emitObject("load", { self: item });
      }
    }
    return true;
  }
  /** Plan 14 — rama Storage SQL legacy (pre-NoSqlInClient). */
  async _loadViaStorageAdapter() {
    const sql = this._buildSelectSql({ raw: false });
    const result = await this._storage.executeSql(sql);
    return result.rows;
  }
  /** Plan 14 — rama estructurada (REST + DSL o catálogo). */
  async _loadViaLinkedTo() {
    if (!this._structuredClient) {
      throw new XoneDataError(
        `Coleccion '${this._name}' declara linkedTo pero no se inyect\xF3 structuredClient. Aseg\xFArate de pasar XoneStructuredDataClient en createDataCollection.`,
        { code: XONE_DATA_ERROR_CODES.LOCKED, collection: this._name }
      );
    }
    const linkedTo = this._definition.linkedTo;
    const ctx = this._buildDslContext();
    if (linkedTo.kind === "rest") {
      const dsl = this._dslBuilder.build({
        filterTemplate: this._definition.filterTemplate,
        order: this._definition.defaultDslOrder
      }, ctx);
      const rows = await this._structuredClient.list(linkedTo.collection, dsl);
      if (this._filter) {
        const developed = this._developMacros(this._filter);
        return this._applyClientFilter(rows, developed);
      }
      return rows;
    }
    if (linkedTo.kind === "cat") {
      const params = this._resolveNamedParams(this._definition.namedParams, ctx);
      return this._structuredClient.runNamed(linkedTo.name, params);
    }
    throw new XoneDataError(
      `Coleccion '${this._name}' usa linkedTo.external='${linkedTo.provider}' \u2014 fuera del alcance del m\xF3dulo Collections (canal externo).`,
      { code: XONE_DATA_ERROR_CODES.LOCKED, collection: this._name }
    );
  }
  _applyClientFilter(rows, filter) {
    const conditions = filter.split(/\s+AND\s+/i).map((c) => c.trim()).filter(Boolean);
    return rows.filter((row) => {
      for (const cond of conditions) {
        const eqIdx = cond.indexOf("=");
        if (eqIdx === -1) continue;
        const rawField = cond.slice(0, eqIdx).trim();
        const field = rawField.replace(/^t\d+\./i, "");
        const value = cond.slice(eqIdx + 1).trim().replace(/^'|'$/g, "");
        const rowVal = row[field] ?? row[field.toLowerCase()] ?? row[field.toUpperCase()];
        if (String(rowVal) !== value) return false;
      }
      return true;
    });
  }
  /**
   * Construye el contexto para `DslBuilder` resolviendo `$ctx` desde el
   * owner DataObject (o el primer fila del filterContext) y `$macro` desde
   * el `globalMacros`.
   */
  _buildDslContext() {
    const owner = this._fieldMacroOwner;
    const macros = this._globalMacros;
    return {
      getCtxField(name) {
        if (!owner) return void 0;
        const upper = name.toUpperCase();
        const v = owner.getValue(upper);
        if (v !== void 0) return v;
        return owner.getValue(name);
      },
      getGlobalMacro(name) {
        if (!macros) return void 0;
        const wrapped = name.startsWith("##") ? name : `##${name}##`;
        const v = macros.getGlobalMacro(wrapped);
        if (v === void 0 || v === null) return void 0;
        return v;
      }
    };
  }
  /**
   * Resuelve parámetros nombrados (Plan 14 §6.1.2): valores literales pasan
   * tal cual; objetos `{ $ctx }` y `{ $macro }` se resuelven con el contexto.
   */
  _resolveNamedParams(params, ctx) {
    if (!params) return {};
    const out = {};
    for (const [key, value] of Object.entries(params)) {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        const v = value;
        if (typeof v.$ctx === "string") {
          out[key] = ctx.getCtxField(v.$ctx) ?? null;
          continue;
        }
        if (typeof v.$macro === "string") {
          out[key] = ctx.getGlobalMacro(v.$macro) ?? null;
          continue;
        }
      }
      out[key] = value;
    }
    return out;
  }
  async reload(_node) {
    this._items = [];
    this._fullyLoaded = false;
    this._currentIndex = -1;
    return this.loadAll();
  }
  async rebuildLayout(_options) {
  }
  async startBrowse(_options) {
    let rows;
    if (this._definition.linkedTo) {
      rows = await this._loadViaLinkedTo();
    } else {
      const sql = this._buildSelectSql({ raw: false });
      const result = await this._storage.executeSql(sql);
      rows = result.rows;
    }
    this._cursor = {
      rows,
      index: 0,
      active: true
    };
    return true;
  }
  isEOF() {
    if (!this._cursor || !this._cursor.active) return true;
    return this._cursor.index >= this._cursor.rows.length;
  }
  async endBrowse() {
    if (!this._cursor) return false;
    this._cursor = null;
    return true;
  }
  browseLength() {
    return this._cursor?.rows.length ?? 0;
  }
  async moveFirst() {
    const cur = this._assertCursor();
    if (cur.rows.length === 0) return false;
    cur.index = 0;
    return true;
  }
  async moveLast() {
    const cur = this._assertCursor();
    if (cur.rows.length === 0) return false;
    cur.index = cur.rows.length - 1;
    return true;
  }
  async moveNext() {
    const cur = this._assertCursor();
    if (cur.index >= cur.rows.length) return false;
    cur.index += 1;
    return cur.index < cur.rows.length;
  }
  async movePrevious() {
    const cur = this._assertCursor();
    if (cur.index <= 0) return false;
    cur.index -= 1;
    return true;
  }
  // -------------------------------------------------------------------------
  // Acceso a items
  // -------------------------------------------------------------------------
  get(indexOrKey) {
    if (typeof indexOrKey === "number") {
      return this._items[indexOrKey] ?? null;
    }
    const idField = this.getIdFieldName();
    for (const it of this._items) {
      if (String(it.getValue(idField)) === indexOrKey) return it;
    }
    return null;
  }
  /**
   * Acceso sync por índice / clave: paridad Android `XoneDataCollection#getObject`
   * y los scripts legacy que hacen `var r = rec.getItem(0); r.TITULO;`
   * (sin await). El método solo lee `_items` ya cargado; `loadAll` se
   * encarga de la parte async.
   */
  getObject(...args) {
    if (args.length === 1) {
      const a = args[0];
      if (typeof a === "number") return this._items[a] ?? null;
      if (typeof a === "string") return this.get(a);
      if (a instanceof DataObject) return a;
    }
    return null;
  }
  /** Alias sync de {@link getObject}. */
  getItem(...args) {
    return this.getObject(...args);
  }
  async findObject(field, value, ...rest) {
    if (!this._fullyLoaded) {
      await this.loadAll();
    }
    const pairs = [[field, value]];
    for (let i = 0; i + 1 < rest.length; i += 2) {
      pairs.push([rest[i], rest[i + 1]]);
    }
    for (const it of this._items) {
      if (pairs.every(([f, v]) => it.getValue(f) === v)) return it;
    }
    return null;
  }
  async findAllObjects(...args) {
    if (!this._fullyLoaded) await this.loadAll();
    if (args.length === 0) return [...this._items];
    const pairs = [];
    for (let i = 0; i + 1 < args.length; i += 2) {
      pairs.push([args[i], args[i + 1]]);
    }
    return this._items.filter(
      (it) => pairs.every(([f, v]) => it.getValue(f) === v)
    );
  }
  // -------------------------------------------------------------------------
  // Creacion / eliminacion / guardado
  // -------------------------------------------------------------------------
  async createObject(initialValues) {
    const initial = { ...initialValues ?? {} };
    if (!initial["ROWID"]) {
      initial["ROWID"] = generateRowId2();
    }
    const obj = this._instantiate(initial, true);
    this._items.push(obj);
    this._reindex();
    const createEv = this._definition.lifecycleEvents?.find(
      (e) => e.eventName === "create" && e.scriptSource.trim().length > 0
    );
    if (createEv && this._runtime) {
      const scope = {
        kind: "object",
        self: obj,
        This: obj,
        ThisDataColl: this,
        locals: {}
      };
      try {
        const r = await this._runtime.executeScript(createEv.scriptSource, scope);
        if (r === false) {
          this._logger.warn(
            `[create] script de <create> en '${this._name}' retorno false; el DataObject se devuelve igualmente (Android propagaria excepcion).`
          );
        }
      } catch (err) {
        this._logger.error(
          `[create] script de <create> en '${this._name}' lanzo:`,
          err
        );
      }
    }
    Object.defineProperty(obj, "__xoneCreateFired", {
      value: true,
      enumerable: false,
      writable: false,
      configurable: false
    });
    if (this._bus) {
      await this._bus.emitObject("create", { self: obj });
    }
    return obj;
  }
  async addItem(item) {
    if (!(item instanceof DataObject) && !(item && typeof item === "object")) {
      return false;
    }
    this._items.push(item);
    this._reindex();
    return true;
  }
  async deleteItem(indexOrItem) {
    const obj = typeof indexOrItem === "number" ? this._items[indexOrItem] ?? null : indexOrItem;
    if (!obj) return false;
    await obj.deleteObject();
    return true;
  }
  async removeItem(indexOrItem) {
    return this.deleteItem(indexOrItem);
  }
  async deleteAll() {
    const copy = [...this._items];
    for (const it of copy) await it.deleteObject();
    return true;
  }
  async browseDeleteAll() {
    return this.deleteAll();
  }
  /** Borrado masivo sin disparar eventos `<before-delete>`/`<after-delete>`. */
  async browseDeleteWithNoRules() {
    const def = this._definition;
    const tableName = (def.tablePrefix ?? "") + def.tableName;
    const where = this.getDevelopedFilter();
    const sql = where ? `DELETE FROM ${tableName} WHERE ${where}` : `DELETE FROM ${tableName}`;
    await this._storage.executeSql(sql);
    this._items = [];
    this._reindex();
    return true;
  }
  clear() {
    this._items = [];
    this._currentIndex = -1;
    this._fullyLoaded = false;
    return true;
  }
  async saveAll() {
    for (const it of this._items) {
      await it.save();
    }
    return true;
  }
  async createClone() {
    const clone = new _DataCollection({
      name: this._name,
      definition: this._definition,
      storage: this._storage,
      macros: this._globalMacros,
      ...this._bus ? { bus: this._bus } : {},
      appData: this._appData,
      parent: this._parent
    });
    clone._filter = this._filter;
    clone._linkFilter = this._linkFilter;
    clone._sort = this._sort;
    return clone;
  }
  async swapItems(indexA, indexB) {
    const a = this._items[indexA];
    const b = this._items[indexB];
    if (!a || !b) return false;
    this._items[indexA] = b;
    this._items[indexB] = a;
    this._reindex();
    return true;
  }
  // -------------------------------------------------------------------------
  // Persistencia / locks / JSON / SQL
  // -------------------------------------------------------------------------
  async lock() {
    this._locked = true;
  }
  async unlock() {
    this._locked = false;
  }
  async loadFromJson(arrayOrJson) {
    let parsed;
    try {
      parsed = typeof arrayOrJson === "string" ? JSON.parse(arrayOrJson) : arrayOrJson;
    } catch (err) {
      throw new XoneDataError("JSON invalido en loadFromJson.", {
        code: XONE_DATA_ERROR_CODES.JSON_INVALID,
        collection: this._name,
        cause: err
      });
    }
    const arr = Array.isArray(parsed) ? parsed : [parsed];
    this._items = arr.map((row) => this._instantiate({ ...row }, false));
    this._reindex();
    this._fullyLoaded = true;
    this._currentIndex = this._items.length > 0 ? 0 : -1;
    return this;
  }
  async toJson(_options) {
    return this._items.map((it) => it.toJson());
  }
  async executeSqlString(sql, params) {
    const developed = this._developMacros(sql);
    const r = await this._storage.executeSql(developed, params);
    return r;
  }
  async createPersistData(_fieldsOrTable, _fields) {
  }
  async doSort(criteria) {
    const sort = criteria ?? this._sort;
    if (!sort) return;
    const parts = sort.split(",").map((s) => s.trim()).filter(Boolean);
    if (parts.length === 0) return;
    this._items.sort((a, b) => {
      for (const part of parts) {
        const [field, dir] = part.split(/\s+/);
        if (!field) continue;
        const va = a.getValue(field);
        const vb = b.getValue(field);
        if (va === vb) continue;
        if (va === null || va === void 0) return 1;
        if (vb === null || vb === void 0) return -1;
        const cmp = va < vb ? -1 : 1;
        return (dir ?? "ASC").toUpperCase() === "DESC" ? -cmp : cmp;
      }
      return 0;
    });
    this._reindex();
  }
  async doSearch(criteria, _tableName) {
    if (!this._fullyLoaded) await this.loadAll();
    const lower = criteria.toLowerCase();
    this._items = this._items.filter((it) => {
      for (const f of this._definition.fields) {
        const v = it.getValue(f.name);
        if (typeof v === "string" && v.toLowerCase().includes(lower)) {
          return true;
        }
      }
      return false;
    });
    this._reindex();
  }
  createSearchIndex(_fieldOrFields, _maybeFields) {
  }
  bind(..._args) {
  }
  unbind(..._args) {
  }
  clearCaches() {
  }
  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------
  _reindex() {
    for (let i = 0; i < this._items.length; i++) {
      const item = this._items[i];
      if (!item) continue;
      item._index = i;
    }
    if (this._currentIndex >= this._items.length) {
      this._currentIndex = this._items.length - 1;
    }
  }
  _assertCursor() {
    if (!this._cursor || !this._cursor.active) {
      throw new XoneDataError("No hay browse activo. Llama startBrowse primero.", {
        code: XONE_DATA_ERROR_CODES.BROWSE_INVALID_STATE,
        collection: this._name
      });
    }
    return this._cursor;
  }
  _developMacros(text) {
    if (!text) return "";
    let s = text;
    if (this._fieldMacroOwner) {
      s = developFieldValueMacros(s, this._fieldMacroOwner, { prefix: "FLD_", suppressQuotes: true });
      s = developFieldValueMacros(s, this._fieldMacroOwner, { prefix: "OWN_", suppressQuotes: true });
    }
    const globalMacros = this._globalMacros ? this._globalMacros.getAllGlobalMacros() : {};
    return replaceMacrosSql(s, {
      globalMacros,
      collectionMacros: this._macros.getAllMacros()
    });
  }
  _buildSelectSql(opts) {
    const def = this._definition;
    const table = (def.tablePrefix ?? "") + def.tableName;
    const wherePieces = [];
    if (this._filter) wherePieces.push(this._filter);
    if (this._linkFilter) wherePieces.push(this._linkFilter);
    const whereSql = wherePieces.length > 0 ? ` WHERE ${wherePieces.join(" AND ")}` : "";
    const orderSql = this._sort ? ` ORDER BY ${this._sort}` : "";
    const sql = `SELECT * FROM ${table}${whereSql}${orderSql}`;
    return opts.raw ? sql : this._developMacros(sql);
  }
};
function createDataCollection(opts) {
  return new DataCollection(opts);
}

// src/adapter/MemoryStorageAdapter.ts
var MemoryStorageAdapter = class {
  tables = /* @__PURE__ */ new Map();
  /** Crea una tabla con columnas (no usado por SQL, solo para inicializar). */
  seedTable(tableName, rows = []) {
    this.tables.set(tableName, { rows: [...rows] });
  }
  async executeSql(sql, params) {
    const trimmed = sql.trim();
    const upper = trimmed.toUpperCase();
    const paramsArr = this.toArray(params);
    if (upper.startsWith("INSERT INTO")) return this.handleInsert(trimmed, paramsArr);
    if (upper.startsWith("UPDATE ")) return this.handleUpdate(trimmed, paramsArr);
    if (upper.startsWith("DELETE FROM")) return this.handleDelete(trimmed, paramsArr);
    if (upper.startsWith("SELECT")) return this.handleSelect(trimmed, paramsArr);
    return { rows: [], affectedRows: 0 };
  }
  async transaction(fn) {
    const snapshot = /* @__PURE__ */ new Map();
    for (const [k, v] of this.tables) snapshot.set(k, [...v.rows]);
    try {
      return await fn(this);
    } catch (err) {
      for (const [k, v] of snapshot) {
        this.tables.set(k, { rows: v });
      }
      throw err;
    }
  }
  isReady() {
    return true;
  }
  // -------------------------------------------------------------------------
  // SQL minimo
  // -------------------------------------------------------------------------
  handleInsert(sql, params) {
    const m = sql.match(
      /^INSERT\s+INTO\s+(\S+)\s*\(([^)]+)\)\s*VALUES\s*\(([^)]+)\)\s*;?\s*$/i
    );
    if (!m) return { rows: [], affectedRows: 0 };
    const tableName = m[1];
    const colsRaw = m[2];
    const cols = colsRaw.split(",").map((s) => s.trim());
    const table = this.ensureTable(tableName);
    const row = {};
    for (let i = 0; i < cols.length; i++) {
      const colName = cols[i];
      if (!colName) continue;
      row[colName] = params[i] ?? null;
    }
    table.rows.push(row);
    return { rows: [], affectedRows: 1 };
  }
  handleUpdate(sql, params) {
    const m = sql.match(
      /^UPDATE\s+(\S+)\s+SET\s+(.+?)\s+WHERE\s+(\S+)\s*=\s*\?\s*;?\s*$/i
    );
    if (!m) return { rows: [], affectedRows: 0 };
    const tableName = m[1];
    const setClauseRaw = m[2];
    const whereField = m[3];
    const setClause = setClauseRaw.split(",").map((s) => s.trim());
    const table = this.tables.get(tableName);
    if (!table) return { rows: [], affectedRows: 0 };
    const whereValue = params[params.length - 1];
    let affected = 0;
    const matchingRows = table.rows.filter(
      (r) => String(r[whereField]) === String(whereValue)
    );
    for (const r of matchingRows) {
      for (let i = 0; i < setClause.length; i++) {
        const part = setClause[i];
        if (!part) continue;
        const fieldMatch = part.match(/^(\S+)\s*=\s*\?$/);
        if (!fieldMatch) continue;
        const fieldName = fieldMatch[1];
        r[fieldName] = params[i] ?? null;
      }
      affected += 1;
    }
    return { rows: [], affectedRows: affected };
  }
  handleDelete(sql, params) {
    const m = sql.match(
      /^DELETE\s+FROM\s+(\S+)(?:\s+WHERE\s+(\S+)\s*=\s*\?)?\s*;?\s*$/i
    );
    if (!m) return { rows: [], affectedRows: 0 };
    const tableName = m[1];
    const whereField = m[2];
    const table = this.tables.get(tableName);
    if (!table) return { rows: [], affectedRows: 0 };
    if (!whereField) {
      const n = table.rows.length;
      table.rows = [];
      return { rows: [], affectedRows: n };
    }
    const whereValue = params[0];
    const before = table.rows.length;
    table.rows = table.rows.filter(
      (r) => String(r[whereField]) !== String(whereValue)
    );
    return { rows: [], affectedRows: before - table.rows.length };
  }
  handleSelect(sql, _params) {
    const m = sql.match(
      /^SELECT\s+(?:\*|[^\s]+(?:\s*,\s*[^\s]+)*)\s+FROM\s+(\S+)(?:\s+WHERE\s+(.+?))?(?:\s+ORDER\s+BY\s+(.+?))?(?:\s+LIMIT\s+\d+)?\s*;?\s*$/i
    );
    if (!m) return { rows: [], affectedRows: 0 };
    const tableName = m[1];
    const where = m[2];
    const orderBy = m[3];
    const table = this.tables.get(tableName);
    if (!table) return { rows: [], affectedRows: 0 };
    let rows = [...table.rows];
    if (where) rows = rows.filter((r) => this.evalSimpleWhere(where, r));
    if (orderBy) rows = this.applyOrderBy(rows, orderBy);
    return { rows, affectedRows: rows.length };
  }
  evalSimpleWhere(where, row) {
    const conds = where.split(/\s+AND\s+/i);
    for (const c of conds) {
      const m = c.match(
        /^\s*([A-Za-z0-9_]+)\s*(=|!=|<>|<=|>=|<|>|LIKE)\s*(.+?)\s*$/i
      );
      if (!m) continue;
      const field = m[1];
      const op = m[2];
      let literal = m[3].trim();
      if (typeof literal === "string" && literal.startsWith("'") && literal.endsWith("'")) {
        literal = literal.slice(1, -1);
      } else if (typeof literal === "string" && /^-?\d+(\.\d+)?$/.test(literal)) {
        literal = Number(literal);
      }
      const v = row[field];
      const opOk = (() => {
        switch (op.toUpperCase()) {
          case "=":
            return String(v) === String(literal);
          case "!=":
          case "<>":
            return String(v) !== String(literal);
          case "<":
            return v < literal;
          case "<=":
            return v <= literal;
          case ">":
            return v > literal;
          case ">=":
            return v >= literal;
          case "LIKE":
            return String(v).includes(String(literal).replace(/%/g, ""));
        }
        return false;
      })();
      if (!opOk) return false;
    }
    return true;
  }
  applyOrderBy(rows, orderBy) {
    const parts = orderBy.split(",").map((s) => s.trim()).filter(Boolean);
    return [...rows].sort((a, b) => {
      for (const p of parts) {
        const [field, dir] = p.split(/\s+/);
        if (!field) continue;
        const va = a[field];
        const vb = b[field];
        if (va === vb) continue;
        if (va == null) return 1;
        if (vb == null) return -1;
        const cmp = va < vb ? -1 : 1;
        return (dir ?? "ASC").toUpperCase() === "DESC" ? -cmp : cmp;
      }
      return 0;
    });
  }
  ensureTable(name) {
    let t = this.tables.get(name);
    if (!t) {
      t = { rows: [] };
      this.tables.set(name, t);
    }
    return t;
  }
  toArray(params) {
    if (!params) return [];
    if (Array.isArray(params)) return [...params];
    return Object.values(params);
  }
  /** Acceso directo para tests (no es parte del contrato IStorageAdapter). */
  _getRowsForTest(tableName) {
    return this.tables.get(tableName)?.rows ?? [];
  }
};

// src/adapters/XoneRemoteStorageAdapter.ts
var READ_RE = /^\s*(SELECT|WITH|PRAGMA|EXPLAIN)\b/i;
var XoneRemoteStorageAdapter = class {
  backend;
  macros;
  apiBase;
  logger;
  constructor(opts) {
    this.backend = opts.backend;
    this.macros = opts.macros;
    this.apiBase = (opts.apiBase ?? "").replace(/\/+$/, "");
    this.logger = opts.logger ?? console;
  }
  isReady() {
    return true;
  }
  /**
   * Plan 14 Fase 4 — `executeSql` está retirado. Cualquier intento lanza
   * inmediatamente con el código <c>XONE.RAW.REMOVED</c>. El adapter sólo
   * sobrevive como shim de compilación; en runtime nunca debe ejecutar SQL.
   *
   * Migración:
   *   - Listas/CRUD simple → `linkedTo` en el `.xne` → REST + DSL via
   *     `XoneStructuredDataClient.list/get/create/update/remove`.
   *   - Queries complejas (joins, subselects, agregados) → catálogo nombrado
   *     pre-compilado (Fase 3 emite las entradas automáticamente).
   */
  async executeSql(_sql, _params) {
    void _sql;
    void _params;
    throw new Error(
      "XONE.RAW.REMOVED: XoneRemoteStorageAdapter.executeSql est\xE1 retirado en Plan 14 Fase 4. El SPA no debe ejecutar SQL crudo en cliente. Migra la colecci\xF3n a `linkedTo` (REST + DSL o cat\xE1logo nombrado). Docs: https://docs.xone.es/migration/raw-removed"
    );
  }
  // Sin TX remota; backend ejecuta autocommit por sentencia.
  async transaction(fn) {
    return fn(this);
  }
  /** No-op: no hay conexion persistente que cerrar. */
  close() {
  }
  // ---------------------------------------------------------------------------
  // Internos
  // ---------------------------------------------------------------------------
  async runRemoteRead(sql, params) {
    const dq = this.backend.dataQuery;
    if (dq && typeof dq.executeRaw === "function") {
      try {
        const res = await dq.executeRaw({ SQL: sql, PARAMS: params });
        return res?.FILAS ?? res?.REGISTROS ?? [];
      } catch (err) {
        this.logger.warn("[XoneRemoteStorageAdapter] executeRaw fallo:", err);
        return [];
      }
    }
    return this.fetchRaw(sql, params);
  }
  async fetchRaw(sql, params) {
    const url = `${this.apiBase}/api/v1/query/raw`;
    const headers = {
      "Content-Type": "application/json",
      "Accept": "application/json"
    };
    const token = this.macros?.getGlobalMacro("TOKEN");
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const f = globalThis.fetch;
    if (!f) {
      this.logger.warn(
        "[XoneRemoteStorageAdapter] No hay fetch disponible; devolviendo [] para:",
        sql
      );
      return [];
    }
    try {
      const res = await f(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ SQL: sql, PARAMS: params })
      });
      if (!res.ok) {
        this.logger.warn(
          `[XoneRemoteStorageAdapter] POST ${url} -> HTTP ${res.status}. Asegurate de que XoneBackendWeb expone el endpoint /api/v1/query/raw.`
        );
        return [];
      }
      const json = await res.json();
      return json?.FILAS ?? json?.REGISTROS ?? [];
    } catch (err) {
      this.logger.warn("[XoneRemoteStorageAdapter] fetch raw fallo:", err);
      return [];
    }
  }
  /**
   * Red de seguridad: expande macros globales `##NAME##` que pudieran
   * llegar sin desarrollar al adapter. Ignora los prefijos que son
   * responsabilidad de `xone-data` (ver `DataCollection._developMacros`
   * y `developFieldValueMacros`) — FLD_, OWN_, ID, CLIENTIDCOLL, MULTIKEY,
   * ISNULL — porque esos requieren un DataObject contextual que el
   * adapter no conoce. Si llegan hasta aquí sin resolver, el caller no
   * inyectó el owner (`DataCollection.setFieldMacroOwner`) y devolver
   * NULL silenciosamente sería incorrecto: dejamos la macro tal cual
   * para que Postgres devuelva un error explícito.
   */
  expandMacros(sql) {
    if (!this.macros || !sql.includes("##")) return sql;
    return sql.replace(/##([A-Z0-9_]+)##/g, (match, name) => {
      if (name.startsWith("FLD_") || name.startsWith("OWN_") || name === "ID" || name === "CLIENTIDCOLL" || name === "MULTIKEY" || name.startsWith("ISNULL")) {
        return match;
      }
      const v = this.macros?.getGlobalMacro(name);
      if (v === null || v === void 0 || v === "") return "NULL";
      const s = String(v);
      if (/^-?\d+(\.\d+)?$/.test(s)) return s;
      return "'" + s.replace(/'/g, "''") + "'";
    });
  }
  toArray(params) {
    if (!params) return [];
    if (Array.isArray(params)) return params;
    return Object.values(params);
  }
  extractTableHint(sql) {
    const m = /(?:INTO|UPDATE|FROM)\s+([A-Za-z_][A-Za-z0-9_]*)/i.exec(sql);
    return m ? m[1] ?? null : null;
  }
};
function createRemoteStorage(opts) {
  const adapter = new XoneRemoteStorageAdapter(opts);
  const dbExecuteSql = async (sql, params) => {
    const r = await adapter.executeSql(sql, params);
    return READ_RE.test(sql) ? r.rows : r.affectedRows;
  };
  return {
    adapter,
    db: {
      executeSql: dbExecuteSql,
      async transaction(fn) {
        return fn({
          executeSql: async (s, p) => dbExecuteSql(s, p)
        });
      },
      close() {
        adapter.close();
      },
      isOpen() {
        return adapter.isReady();
      }
    }
  };
}

// src/clients/XoneStructuredDataClient.ts
function parseLinkedTo(raw) {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (trimmed.startsWith("rest:")) {
    const collection = trimmed.slice(5).trim();
    if (!collection) return null;
    return { kind: "rest", collection };
  }
  if (trimmed.startsWith("cat:")) {
    const name = trimmed.slice(4).trim();
    if (!name) return null;
    return { kind: "cat", name };
  }
  if (trimmed.startsWith("external:")) {
    const provider = trimmed.slice(9).trim();
    if (!provider) return null;
    return { kind: "external", provider };
  }
  return null;
}
var XoneStructuredDataClient = class {
  constructor(backend) {
    this.backend = backend;
  }
  backend;
  /** REST list. */
  async list(collection, dsl) {
    const resp = await this.backend.collection.list(collection, dsl);
    return resp.ROWS;
  }
  /** REST list devolviendo la respuesta completa (page metadata + cursor). */
  async listPage(collection, dsl) {
    return this.backend.collection.list(collection, dsl);
  }
  async get(collection, rowid) {
    const resp = await this.backend.collection.get(collection, rowid);
    return resp.ROW;
  }
  async count(collection, filter) {
    const resp = await this.backend.collection.count(collection, filter);
    return resp.TOTAL;
  }
  async create(collection, row) {
    const resp = await this.backend.collection.create(collection, row);
    return resp.ROWID;
  }
  async update(collection, rowid, row) {
    await this.backend.collection.update(collection, rowid, row);
  }
  async remove(collection, rowid) {
    await this.backend.collection.remove(collection, rowid);
  }
  /**
   * Catálogo nombrado. El nombre se prefija con `projectId` en build-time
   * (el cliente recibe ya el nombre completo `<projectId>.<queryName>`).
   */
  async runNamed(name, parametros = {}, opts) {
    const orden = opts?.order?.map((o) => ({
      CAMPO: o.field,
      DIRECCION: (o.dir ?? "asc").toUpperCase()
    }));
    const paginacion = opts?.offset !== void 0 || opts?.limit !== void 0 ? { OFFSET: opts?.offset ?? 0, LIMIT: opts?.limit ?? 100 } : void 0;
    const resp = await this.backend.dataQuery.execute(name, {
      PARAMETROS: parametros,
      PAGINACION: paginacion,
      ORDEN: orden
    });
    return resp.REGISTROS;
  }
};
export {
  DataCollection,
  DataObject,
  DirtyTracker,
  DslBuilder,
  MemoryStorageAdapter,
  XONE_DATA_ERROR_CODES,
  XoneDataError,
  XoneRemoteStorageAdapter,
  XoneStructuredDataClient,
  assertUpperCaseField,
  createDataCollection,
  createDataObject,
  createRemoteStorage,
  developFieldValueMacros,
  developObjectValue,
  parseLinkedTo,
  wrapWithProxy
};
//# sourceMappingURL=index.js.map